/**
 * The Talk tab: scenario library, scenario detail, open talk, custom role-play and the
 * tutor picker (docs/04-screen-specs.md D1–D9).
 *
 * The library is the point. Market leaders ship mundane, specific scenario titles —
 * "return a sweater that doesn't fit" — because "practise free conversation" is an
 * invitation to freeze, and the competitor's open-ended prompt is a large part of why
 * its sessions are short.
 */

import type { RouteResponse } from '@saysooner/api-client';
import { clientUlid } from '@saysooner/api-client';
import { Banner, Button, EmptyState, ErrorState, Skeleton } from '@saysooner/ui';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { qk, retryIf } from '../lib/client.js';

type ScenarioList = RouteResponse<'listScenarios'>;
type ScenarioDetail = RouteResponse<'getScenario'>;

export interface NavigationProps {
  readonly onNavigate: (route: string) => void;
}

/**
 * The three rails, and why two of these subtitles are worded the way they are.
 *
 * "For you" used to say "Chosen from what you got wrong this week" and
 * "Popular" said "...at your level" — while the server applied no filter for either
 * rail and nothing had ever set `is_trending`. Both claims are now acted on: `for_you`
 * orders by the learner's due and shaky cards, `trending` is recomputed weekly from
 * distinct learners per dialect. The copy says only what those two actually do — the
 * `for_you` list still falls back to course order for a learner with no history, so it
 * cannot claim to be built from their mistakes, and trending is counted per dialect
 * rather than per CEFR level.
 */
const RAILS = [
  ['for_you', 'For you', 'Picked for where you are right now'],
  ['trending', 'Popular', 'What other learners are practising this week'],
  ['from_your_mistakes', 'From your mistakes', 'Conversations built around your own errors'],
] as const;

const CATEGORIES = [
  ['daily_life', 'Daily life'],
  ['work', 'Work'],
  ['travel', 'Travel'],
  ['health', 'Health'],
  ['services', 'Services'],
  ['social', 'Social'],
  ['family', 'Family'],
  ['exam', 'Exam'],
] as const;

// ── D1 Talk hub ─────────────────────────────────────────────────────────────

export function TalkHubScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { requestPaywall, track } = useApp();
  const [rail, setRail] = useState<(typeof RAILS)[number][0]>('for_you');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number][0] | null>(null);

  /**
   * Paged, because a course can now hold more scenarios than one request returns.
   *
   * es-MX has 55 and the server's page is 20 by default, 100 at most. Asking for a
   * single page of 30 and ignoring `nextCursor` — which is what this did — left 25 of
   * them with no way to be reached from the UI at all. `useInfiniteQuery` follows the
   * cursor the server now issues, and the rail renders every page it has.
   */
  const query = useInfiniteQuery({
    queryKey: qk.scenarios(courseId, `${rail}:${category ?? 'all'}`),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.call('listScenarios', {
        query: {
          courseId,
          rail,
          limit: 50,
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
          ...(category === null ? {} : { category }),
        },
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // Flattened once, so every consumer below reads one list rather than a page shape.
  const scenarios = (query.data?.pages ?? []).flatMap((page) => page.items);

  // Pull the rest in the background: a learner scrolling a rail should not meet a
  // "load more" button for content that is already a single round trip away.
  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage, query]);

  const railCopy = RAILS.find(([id]) => id === rail);

  // Minutes left is the number this screen is really about: someone arriving with two
  // minutes of voice left has a different experience of it from someone with twenty,
  // and the mode they pick is the thing worth correlating with it.
  const entitlements = useQuery({
    queryKey: qk.entitlements,
    queryFn: () => api.call('getEntitlements', {}),
  });
  const usage = entitlements.data?.usage;
  const advancedModes = entitlements.data?.entitlement.limits.advancedModes === true;
  useEffect(() => {
    if (usage === undefined) return;
    track('talk_home_view', {
      minutes_left:
        usage.voiceSecondsTotal === null
          ? null
          : Math.max(0, Math.round((usage.voiceSecondsTotal - usage.voiceSecondsUsed) / 60)),
    });
  }, [usage, track]);

  // The library view, with the filters actually applied. "Which rail do people browse"
  // decides what the recommender should be ranking in the first place.
  useEffect(() => {
    track('scenario_library_view', {
      filters: [rail, category ?? 'all'],
    });
  }, [rail, category, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Talk</h1>

      <nav className="vl-rail-tabs" aria-label="Scenario collections">
        {RAILS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="vl-rail-tab"
            aria-pressed={rail === id}
            onClick={() => {
              track('roleplay_rail_tapped', { rail: id });
              setRail(id);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      {railCopy !== undefined && <p className="vl-page__lede">{railCopy[2]}</p>}

      {/* Search, because a library of scenarios you can only browse by rail is a
          library you cannot find anything specific in — and "I need to talk to a
          landlord tomorrow" is the most motivated a learner ever is. */}
      <search>
        <form
          className="vl-field"
          onSubmit={(event) => {
            event.preventDefault();
            const results = scenarios.filter((scenario) =>
              scenario.title.toLowerCase().includes(search.trim().toLowerCase()),
            ).length;
            // The query and the result count together: a search with no results is
            // the most useful thing this event can tell us, because it is a content
            // gap someone was actively looking for.
            track('scenario_search', { query: search.trim(), results });
          }}
        >
          <label className="vl-field__label" htmlFor="vl-scenario-search">
            Search scenarios
          </label>
          <input
            id="vl-scenario-search"
            className="vl-field__input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="renting a flat"
          />
        </form>
      </search>

      <nav className="vl-chip-filters" aria-label="Filter by category">
        <button
          type="button"
          className="vl-chip-filter"
          aria-pressed={category === null}
          onClick={() => setCategory(null)}
        >
          All
        </button>
        {CATEGORIES.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="vl-chip-filter"
            aria-pressed={category === id}
            onClick={() => setCategory(category === id ? null : id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {query.isPending && <Skeleton lines={5} label="Loading conversations" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load conversations"
          {...retryIf(query.error, () => void query.refetch())}
          alternative={{ label: 'Open review instead', onClick: () => onNavigate('/review') }}
        />
      )}

      {query.data !== undefined && (
        <ScenarioGrid
          scenarios={scenarios.filter(
            (scenario) =>
              search.trim().length === 0 ||
              scenario.title.toLowerCase().includes(search.trim().toLowerCase()),
          )}
          onOpen={(scenario) => {
            // Whether it was locked is the whole point: a locked card tapped often is
            // the strongest signal we have that the gate is in the wrong place.
            track('scenario_card_tapped', { id: scenario.id, locked: scenario.isLocked });
            if (scenario.isLocked) {
              requestPaywall('scenario_locked');
              return;
            }
            onNavigate(`/talk/scenario/${scenario.id}`);
          }}
        />
      )}

      <section className="vl-talk__other">
        <h2>Other ways to talk</h2>
        <Button
          variant="secondary"
          onClick={() => {
            track('talk_mode_tapped', { mode: 'open_talk' });
            onNavigate('/talk/open');
          }}
        >
          Open Talk — no script, just conversation
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            track('talk_mode_tapped', { mode: 'roleplay' });
            onNavigate('/talk/roleplay/new');
          }}
        >
          Describe your own situation
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            // "Surprise me" is a real product affordance rather than a filler: a
            // learner who cannot choose is a learner who does not practise.
            track('roleplay_surprise', {});
            onNavigate('/talk/roleplay/new');
          }}
        >
          Surprise me
        </Button>
        {/*
          Debate and Interview were built, specced and wired for analytics, and
          nothing in any client navigated to either — so neither had ever been
          reachable by a learner. D8's own spec says the Plus modes are "shown locked
          rather than hidden ... a feature nobody can see cannot be wanted", which is
          exactly the argument against leaving them with no entry point at all.
        */}
        {/*
          Shown locked, not hidden, and the lock is real. The entry points were added
          when these screens turned out to be unreachable, and the lock was not added
          with them — so a free learner could finish both, while the tier table sells
          them at "— | ✓ | ✓". `limits.advancedModes` is the boundary now, and the
          Worker refuses the session too, because a client-side lock is the half that
          can be bypassed.
        */}
        <Button
          variant="secondary"
          onClick={() => {
            track('talk_mode_tapped', { mode: 'debate' });
            if (!advancedModes) {
              requestPaywall('advanced_modes');
              return;
            }
            onNavigate('/talk/debate');
          }}
        >
          {advancedModes ? 'Debate — argue a side and defend it' : 'Debate — part of Plus'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            track('talk_mode_tapped', { mode: 'interview' });
            if (!advancedModes) {
              requestPaywall('advanced_modes');
              return;
            }
            onNavigate('/talk/interview');
          }}
        >
          {advancedModes ? 'Interview practice' : 'Interview practice — part of Plus'}
        </Button>
        <Button variant="ghost" onClick={() => onNavigate('/talk/tutors')}>
          Choose who you talk to
        </Button>
      </section>
    </main>
  );
}

function ScenarioGrid({
  scenarios,
  onOpen,
}: {
  readonly scenarios: ScenarioList['items'];
  readonly onOpen: (scenario: ScenarioList['items'][number]) => void;
}): JSX.Element {
  if (scenarios.length === 0) {
    return (
      <EmptyState
        title="Nothing here yet"
        body="New conversations land every week."
        action={{ label: 'Browse all', onClick: () => undefined }}
      />
    );
  }

  return (
    <ul className="vl-scenario-grid">
      {scenarios.map((scenario) => (
        <li key={scenario.id}>
          <button
            type="button"
            className="vl-scenario-card"
            data-locked={scenario.isLocked}
            onClick={() => onOpen(scenario)}
          >
            <span className="vl-scenario-card__title">{scenario.title}</span>
            {/* The complication, not the topic — this is what makes it practisable. */}
            <span className="vl-scenario-card__brief">{scenario.brief}</span>
            <span className="vl-scenario-card__meta">
              {scenario.cefr} · {scenario.estimatedMinutes[0]}–{scenario.estimatedMinutes[1]} min
              {scenario.isTrending && ' · popular'}
            </span>
            {scenario.vocabPreview.length > 0 && (
              <span className="vl-scenario-card__vocab">{scenario.vocabPreview.join(' · ')}</span>
            )}
            {scenario.isLocked && <span className="vl-scenario-card__lock">Plus</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── D2 Scenario detail ──────────────────────────────────────────────────────

export function ScenarioScreen({
  scenarioId,
  onNavigate,
}: NavigationProps & { readonly scenarioId: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['scenario', scenarioId] as const,
    queryFn: () => api.call('getScenario', { params: { scenarioId } }),
  });

  useEffect(() => {
    track('scenario_detail_view', { id: scenarioId });
  }, [scenarioId, track]);

  if (query.isPending) return <Skeleton lines={6} label="Loading this conversation" />;
  if (query.error !== null || query.data === undefined) {
    return (
      <ErrorState
        title="We couldn't load this conversation"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Back to Talk', onClick: () => onNavigate('/talk') }}
      />
    );
  }

  const scenario: ScenarioDetail = query.data;

  return (
    <main id="main" className="vl-page">
      <h1>{scenario.title}</h1>
      <p className="vl-page__lede">{scenario.brief}</p>

      <dl className="vl-scenario-roles">
        <div>
          <dt>You are</dt>
          <dd>{scenario.yourRole}</dd>
        </div>
        <div>
          <dt>They are</dt>
          <dd>{scenario.theirRole}</dd>
        </div>
      </dl>

      {/* Stated before starting, so "did I do well?" has an answer the learner can
          check themselves against rather than a vibe. */}
      <section>
        <h2>What counts as doing it</h2>
        <ul className="vl-criteria">
          {scenario.successCriteria.map((criterion) => (
            <li key={criterion.id}>{criterion.description}</li>
          ))}
        </ul>
      </section>

      {scenario.targetPhrases.length > 0 && (
        <section>
          <h2>Phrases that will help</h2>
          <ul className="vl-phrase-list">
            {scenario.targetPhrases.map((phrase) => (
              <li key={phrase.text}>
                <span className="vl-phrase__text">{phrase.text}</span>
                <span className="vl-phrase__translation">{phrase.translation}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Only for a scenario this learner wrote. `scenario.sharing` is null on everything
        else, which is the server saying "do not offer this" rather than the client
        guessing from whether the id looks custom.
      */}
      {scenario.sharing !== null && (
        <ShareControl scenarioId={scenarioId} sharing={scenario.sharing} />
      )}

      <Button
        onClick={() => {
          // The gap between `scenario_detail_view` and this is the interesting number:
          // a brief that nobody starts is a brief that is putting people off.
          track('scenario_start', { id: scenarioId, mode: 'voice' });
          onNavigate(`/talk/scenario/${scenarioId}/session`);
        }}
      >
        Start the conversation
      </Button>
      {/*
        The same scenario by text. Built as `TextingScenarioScreen`, specced, and
        reachable from nowhere until now — which made it a mode that existed only for
        whoever typed the URL. It belongs here rather than on Talk home: the choice is
        *how* to practise this brief, not which brief to practise.
      */}
      <Button
        variant="secondary"
        onClick={() => {
          track('scenario_start', { id: scenarioId, mode: 'text' });
          onNavigate(`/talk/texting/${scenarioId}`);
        }}
      >
        Practise this by text instead
      </Button>
      {/* Reading the phrases first is a legitimate way to prepare, not cheating. */}
      <p className="vl-note">Take as long as you like with the phrases first — nothing is timed.</p>
    </main>
  );
}

type SharingBlock = NonNullable<ScenarioDetail['sharing']>;

/**
 * The author's share panel for one scenario they wrote.
 *
 * Shows the count before the button when there is one, because "4 people have tried
 * this" is the only reward the loop gives an author and it is the reason to share again.
 */
function ShareControl({
  scenarioId,
  sharing,
}: {
  readonly scenarioId: string;
  readonly sharing: SharingBlock;
}): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const [state, setState] = useState<SharingBlock>(sharing);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: (shared: boolean) =>
      api.call('setScenarioSharing', { params: { scenarioId }, body: { shared } }),
    onSuccess: (result, shared) => {
      setState(result);
      setCopied(false);
      track(shared ? 'scenario_shared' : 'scenario_unshared', { id: scenarioId });
    },
  });

  const url =
    state.shareToken === null ? null : `${window.location.origin}/shared/${state.shareToken}`;

  return (
    <section className="vl-share">
      <h2>Share this one</h2>
      {state.timesRun > 0 && (
        <p className="vl-share__count">
          {state.timesRun === 1
            ? 'One other person has practised this.'
            : `${state.timesRun} other people have practised this.`}
        </p>
      )}

      {url === null ? (
        <>
          <p className="vl-note">
            Anyone you send the link to can practise it, with or without an account. You can turn it
            off again whenever you like.
          </p>
          <Button onClick={() => mutation.mutate(true)} disabled={mutation.isPending}>
            Create a link
          </Button>
        </>
      ) : (
        <>
          {/* Read-only rather than a bare string: a link you cannot select is a link
              you cannot send from a browser that blocks the clipboard. */}
          <input className="vl-share__url" readOnly value={url} aria-label="Share link" />
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(url).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => mutation.mutate(false)}
            disabled={mutation.isPending}
          >
            Stop sharing
          </Button>
          <p className="vl-note">Stopping works immediately — the link dies for everyone.</p>
        </>
      )}

      {mutation.isError && (
        <Banner tone="warning">
          We couldn't change the sharing for this one. Try again in a moment.
        </Banner>
      )}
    </section>
  );
}

// ── D14 Shared role-play ────────────────────────────────────────────────────

/**
 * Where a shared link lands, for someone who may have no account at all.
 *
 * This is the only screen in the product that renders with no course and no token, so
 * it takes nothing from context. It shows enough to decide — the brief, the two roles,
 * how long it takes — and then asks for the one thing it needs, which is a learner.
 */
export function SharedScenarioScreen({
  shareToken,
  onNavigate,
}: NavigationProps & { readonly shareToken: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['shared-scenario', shareToken] as const,
    queryFn: () => api.call('getSharedScenario', { params: { shareToken } }),
  });

  useEffect(() => {
    track('shared_scenario_view', { token: shareToken });
  }, [shareToken, track]);

  if (query.isPending) return <Skeleton lines={5} label="Loading this role-play" />;

  // A revoked link and a link that never existed are one message, deliberately: telling
  // a stranger "this was shared and then taken down" says something about the author
  // that the author did not choose to say.
  if (query.error !== null || query.data === undefined) {
    return (
      <EmptyState
        title="That link doesn't work any more"
        body="Whoever sent it may have stopped sharing. You can still start anywhere you like."
        action={{ label: 'See what else there is', onClick: () => onNavigate('/talk') }}
      />
    );
  }

  const shared = query.data;

  return (
    <main id="main" className="vl-page">
      <p className="vl-page__eyebrow">
        {shared.authorLabel === null
          ? 'Someone shared a role-play with you'
          : `${shared.authorLabel} shared a role-play with you`}
      </p>
      <h1>{shared.title}</h1>
      <p className="vl-page__lede">{shared.brief}</p>

      <dl className="vl-scenario-roles">
        <div>
          <dt>You would be</dt>
          <dd>{shared.yourRole}</dd>
        </div>
        <div>
          <dt>They would be</dt>
          <dd>{shared.theirRole}</dd>
        </div>
      </dl>

      <p className="vl-note">
        {shared.cefr} · about {shared.estimatedMinutes[0]}–{shared.estimatedMinutes[1]} minutes
        {shared.timesRun > 0 && ` · ${shared.timesRun} people have tried it`}
      </p>

      <Button
        onClick={() => {
          track('shared_scenario_start', { token: shareToken });
          onNavigate(`/talk/scenario/${shared.scenarioId}`);
        }}
      >
        Practise this
      </Button>
      {/* Said plainly, because the alternative is a signup wall and that is the thing
          this screen exists to avoid being. */}
      <p className="vl-note">No account needed to look. You can start speaking in a minute.</p>
    </main>
  );
}

// ── D5 Open Talk ────────────────────────────────────────────────────────────

export function OpenTalkScreen({ onNavigate }: NavigationProps): JSX.Element {
  const { requestPaywall, track } = useApp();

  return (
    <main id="main" className="vl-page">
      <h1>Open Talk</h1>
      <p className="vl-page__lede">
        No script. Talk about whatever you like and we'll keep up, correcting afterwards rather than
        interrupting.
      </p>

      {/* Honest about the trade-off: open conversation is harder, and saying so is
          better than letting someone freeze and conclude they cannot speak. */}
      <Banner tone="info">
        Open Talk is harder than a scenario. If you run dry, a scenario gives you something concrete
        to push against.
      </Banner>

      <Button
        onClick={() => {
          // `opener_source` says who chose the subject. Open Talk's claim is that the
          // learner leads, so a run of tutor-chosen openers would mean the opposite is
          // happening in practice.
          track('open_talk_start', { opener_source: 'learner' });
          onNavigate('/talk/open/session');
        }}
      >
        Start talking
      </Button>
      <Button variant="secondary" onClick={() => onNavigate('/talk/scenarios')}>
        Give me a situation instead
      </Button>
      <Button variant="ghost" onClick={() => requestPaywall('deep_analysis')}>
        What do I get afterwards?
      </Button>
    </main>
  );
}

// ── D6 Custom role-play ─────────────────────────────────────────────────────

export function RoleplayBuilderScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { requestPaywall, track } = useApp();

  const [brief, setBrief] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');

  const create = useMutation({
    mutationFn: () =>
      api.call('createCustomScenario', {
        // Client-generated id, so a retry after a timeout resolves to the same
        // scenario rather than spending a second one from the daily allowance.
        body: { id: clientUlid(), courseId, brief, difficulty },
      }),
    onSuccess: (scenario) => onNavigate(`/talk/scenario/${scenario.id}`),
    onError: (error: unknown) => {
      // A spent custom-scenario allowance is a limit, not a failure, and the limit
      // sheet offers the free library rather than a dead end.
      if (error instanceof Error && error.message.includes('quota')) {
        requestPaywall('custom_scenario_limit');
      }
    },
  });

  return (
    <main id="main" className="vl-page">
      <h1>Your own situation</h1>
      <p className="vl-page__lede">
        Describe something you actually have to do. The more specific, the more useful.
      </p>

      <label className="vl-field">
        <span className="vl-field__label">What's the situation?</span>
        <textarea
          className="vl-field__input"
          rows={3}
          value={brief}
          maxLength={400}
          placeholder="I have to tell my landlord the heating has been broken for a week, and he keeps saying he'll send someone"
          onChange={(event) => setBrief(event.target.value)}
        />
      </label>

      <fieldset className="vl-field">
        <legend className="vl-field__label">How hard should they make it?</legend>
        {(['easy', 'medium', 'hard'] as const).map((level) => (
          <label key={level} className="vl-radio">
            <input
              type="radio"
              name="difficulty"
              checked={difficulty === level}
              onChange={() => setDifficulty(level)}
            />
            {level === 'easy' ? 'Cooperative' : level === 'medium' ? 'Realistic' : 'Difficult'}
          </label>
        ))}
      </fieldset>

      <Button
        onClick={() => {
          // The length of the brief, not its text: how much someone is willing to
          // write is the signal, and the brief itself is the learner's own content.
          track('roleplay_custom_created', { prompt_len: brief.trim().length });
          create.mutate();
        }}
        disabled={brief.trim().length < 10}
        loading={create.isPending}
      >
        Build it
      </Button>

      {create.error !== null && (
        <ErrorState
          title="We couldn't build that one"
          detail="Nothing was used up. Try rewording it, or pick one from the library."
          {...retryIf(create.error, () => create.mutate())}
          alternative={{
            label: 'Browse the library',
            onClick: () => onNavigate('/talk/scenarios'),
          }}
        />
      )}
    </main>
  );
}

// ── D7 Tutor picker ─────────────────────────────────────────────────────────

const PERSONAS = [
  ['warm', 'Warm', 'Encouraging, lets small slips go'],
  ['brisk', 'Brisk', 'Talks at native pace and expects you to keep up'],
  ['patient', 'Patient', 'Slower, waits for you, repeats without being asked'],
  ['formal', 'Formal', 'Polite register — useful before a work call'],
  ['chatty', 'Chatty', 'Rambles, interrupts a bit, like a real person'],
  ['vex', 'Vex', 'Sarcastic and unimpressed. Not for a bad day.'],
] as const;

export function TutorPickerScreen({ onNavigate }: NavigationProps): JSX.Element {
  const { track } = useApp();
  const [selected, setSelected] = useState<string>('warm');

  return (
    <main id="main" className="vl-page">
      <h1>Who are you talking to?</h1>
      <p className="vl-page__lede">You can change this at any point, including mid-conversation.</p>

      <ul className="vl-persona-list">
        {PERSONAS.map(([id, name, detail]) => (
          <li key={id}>
            <button
              type="button"
              className="vl-persona"
              aria-pressed={selected === id}
              onClick={() => {
                // `from` and `to` together: switching *away* from a persona is the
                // interesting direction, and especially away from Vex, whose whole
                // design is to be hard to stay with.
                track('tutor_changed', { from: selected, to: id, scope: 'default' });
                setSelected(id);
              }}
            >
              <span className="vl-persona__name">{name}</span>
              <span className="vl-persona__detail">{detail}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* A difficult interlocutor is a feature, but springing one on someone is not. */}
      {selected === 'vex' && (
        <Banner tone="warning">
          Vex pushes back and does not hand out encouragement. Pick someone else if today is not the
          day.
        </Banner>
      )}

      <Button onClick={() => onNavigate('/talk')}>Use this tutor</Button>
    </main>
  );
}

// ── D8 Debate mode ──────────────────────────────────────────────────────────

const DEBATE_TOPICS: readonly { readonly id: string; readonly claim: string }[] = [
  { id: 'remote_work', claim: 'Working from home is better for everyone' },
  { id: 'social_media', claim: 'Social media has done more harm than good' },
  { id: 'city_cars', claim: 'City centres should ban private cars' },
  { id: 'school_uniform', claim: 'School uniforms should be compulsory' },
];

/**
 * Pick a position; the tutor argues the opposite.
 *
 * Scored additionally on argument structure and discourse markers, because that is
 * what a debate practises that a scenario does not: "on the other hand", "that may be
 * true, but" — the connective tissue that makes someone sound like they are reasoning
 * rather than listing.
 *
 * Locked for Pro, and shown locked rather than hidden. A feature nobody can see cannot
 * be wanted, and hiding the ceiling is how a learner discovers it by accident later.
 */
export function DebateScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { requestPaywall, track, isSubscribed } = useApp();

  const [topic, setTopic] = useState<string | null>(null);
  const [side, setSide] = useState<'for' | 'against'>('for');

  const start = useMutation({
    mutationFn: () =>
      api.call(
        'createSession',
        {
          body: {
            id: clientUlid(),
            courseId,
            kind: 'debate',
            refId: null,
            // The topic and the side are what the tutor argues against. They used to
            // reach the server only as an idempotency key, so every debate opened with
            // no idea what it was about.
            subject: topic === null ? null : `${topic} (the learner argues ${side})`,
            mode: 'voice',
          },
        },
        { idempotencyKey: topic ?? 'debate' },
      ),
    onSuccess: (created) => onNavigate(`/talk/session/${created.session.id}`),
  });

  return (
    <main id="main" className="vl-page vl-debate">
      <h1>Debate</h1>
      <p className="vl-page__lede">
        Pick a side. The tutor takes the other one and does not go easy — you are scored on how you
        build an argument as well as on how you say it.
      </p>

      {!isSubscribed && (
        <Banner
          tone="info"
          // `advanced_modes`, not `exam_track`: the hub already used the right trigger and
          // these two screens asked for the exam-track comparison paywall instead.
          action={{ label: 'See what Plus adds', onClick: () => requestPaywall('advanced_modes') }}
        >
          Debate is part of Plus. It is here so you can see what it is, not to tease you — the free
          conversation modes are not time-limited.
        </Banner>
      )}

      <fieldset className="vl-debate__topics">
        <legend>What are we arguing about?</legend>
        {DEBATE_TOPICS.map((candidate) => (
          <label key={candidate.id} className="vl-choice">
            <input
              type="radio"
              name="topic"
              checked={topic === candidate.id}
              onChange={() => setTopic(candidate.id)}
            />
            {candidate.claim}
          </label>
        ))}
      </fieldset>

      <fieldset className="vl-debate__side">
        <legend>Your position</legend>
        {(['for', 'against'] as const).map((option) => (
          <label key={option} className="vl-choice">
            <input
              type="radio"
              name="side"
              checked={side === option}
              onChange={() => setSide(option)}
            />
            {option === 'for' ? 'I agree with it' : 'I disagree with it'}
          </label>
        ))}
      </fieldset>

      <Button
        disabled={topic === null || start.isPending}
        loading={start.isPending}
        onClick={() => {
          if (topic === null) return;
          // The topic and the side together: which side people choose on a given claim
          // is what tells us whether a topic is actually arguable or just agreed with.
          track('debate_start', { topic, side });
          if (!isSubscribed) {
            requestPaywall('exam_track');
            return;
          }
          start.mutate();
        }}
      >
        Start the debate
      </Button>
    </main>
  );
}

// ── D9 Interview mode ───────────────────────────────────────────────────────

const INTERVIEW_TRACKS: readonly {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
}[] = [
  { id: 'job', label: 'Job interview', detail: 'Competency questions, with follow-ups.' },
  { id: 'visa', label: 'Visa interview', detail: 'Short, factual, and deliberately brisk.' },
  { id: 'ielts_part2', label: 'IELTS Part 2', detail: 'One minute to prepare, two to speak.' },
  { id: 'dele_oral', label: 'DELE oral', detail: 'Description and opinion, timed.' },
];

/**
 * Interview simulations with authentic timing.
 *
 * The timing is the point: IELTS Part 2 gives you one minute to prepare and two to
 * speak, and practising it untimed teaches the wrong thing. A learner with no exam
 * track selected gets the generic job interview rather than a block — offering nothing
 * is worse than offering the near thing.
 */
export function InterviewScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { requestPaywall, track, isSubscribed } = useApp();
  const [trackId, setTrackId] = useState<string>('job');

  const start = useMutation({
    mutationFn: () =>
      api.call(
        'createSession',
        {
          body: {
            id: clientUlid(),
            courseId,
            kind: 'interview',
            refId: null,
            subject: trackId,
            mode: 'voice',
          },
        },
        { idempotencyKey: trackId },
      ),
    onSuccess: (created) => onNavigate(`/talk/session/${created.session.id}`),
  });

  return (
    <main id="main" className="vl-page vl-interview">
      <h1>Interview practice</h1>
      <p className="vl-page__lede">
        Real timing, real follow-ups, and a score mapped to the rubric the exam actually uses.
      </p>

      {!isSubscribed && (
        <Banner
          tone="info"
          action={{ label: 'See what Plus adds', onClick: () => requestPaywall('advanced_modes') }}
        >
          Interview practice is part of Plus.
        </Banner>
      )}

      <fieldset className="vl-interview__tracks">
        <legend>Which one?</legend>
        {INTERVIEW_TRACKS.map((candidate) => (
          <label key={candidate.id} className="vl-choice">
            <input
              type="radio"
              name="track"
              checked={trackId === candidate.id}
              onChange={() => setTrackId(candidate.id)}
            />
            <span>
              {candidate.label}
              <small>{candidate.detail}</small>
            </span>
          </label>
        ))}
      </fieldset>

      <Button
        loading={start.isPending}
        onClick={() => {
          track('interview_start', { track: trackId });
          if (!isSubscribed) {
            requestPaywall('exam_track');
            return;
          }
          start.mutate();
        }}
      >
        Begin
      </Button>
    </main>
  );
}

// ── C14 Texting scenario ────────────────────────────────────────────────────

/**
 * Written chat with the tutor, in a messaging UI rather than a lesson UI.
 *
 * The register is the lesson here. A learner who can write a formal paragraph often
 * cannot write "running late, sorry!" — informality is a skill with its own rules, and
 * a textbook exercise cannot teach it because a textbook never sounds like a text
 * message. Free tier, because text is cheap to serve and the speaking cap is the only
 * limit this product needs.
 *
 * Corrections appear inline as a diff rather than as a list afterwards: in writing, the
 * learner can still see what they wrote, so showing the change in place is the clearest
 * form the feedback can take.
 */
export function TextingScenarioScreen({
  scenarioId,
  onNavigate,
}: NavigationProps & { readonly scenarioId: string }): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<
    readonly {
      readonly role: 'learner' | 'tutor';
      readonly text: string;
      readonly correction: string | null;
    }[]
  >([]);
  const corrections = messages.filter((message) => message.correction !== null).length;

  const session = useQuery({
    queryKey: ['texting', scenarioId] as const,
    queryFn: () =>
      api.call('createSession', {
        body: { id: clientUlid(), courseId, kind: 'scenario', refId: scenarioId, mode: 'text' },
      }),
  });

  // Emitted when the conversation ends rather than per message: the useful shape is
  // "how long did it run and how much needed correcting", and a per-message event
  // would drown that in noise.
  const report = useCallback(() => {
    if (messages.length === 0) return;
    track('texting_session', {
      scenario: scenarioId,
      turns: messages.filter((message) => message.role === 'learner').length,
      corrections,
    });
  }, [messages, corrections, scenarioId, track]);

  useEffect(() => report, [report]);

  if (session.isPending) return <Skeleton lines={5} label="Opening the chat" />;
  if (session.error !== null) {
    return (
      <ErrorState
        title="We couldn't open this chat"
        {...retryIf(session.error, () => void session.refetch())}
        alternative={{ label: 'Back to Talk', onClick: () => onNavigate('/talk') }}
      />
    );
  }

  const send = () => {
    const text = draft.trim();
    if (text.length === 0) return;
    setMessages((current) => [...current, { role: 'learner', text, correction: null }]);
    setDraft('');
  };

  return (
    <main id="main" className="vl-page vl-texting">
      <h1 className="vl-texting__title">Texting</h1>
      <p className="vl-page__lede">
        Write the way you would actually text. Short, informal, abbreviations and all — that is what
        is being practised.
      </p>

      <ol className="vl-texting__thread" aria-live="polite">
        {messages.map((message, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: a thread is append-only and never reorders, so position is identity.
            key={`${message.role}-${index}`}
            className="vl-texting__message"
            data-role={message.role}
          >
            <p>{message.text}</p>
            {/* The diff in place, because the learner can still see what they wrote. */}
            {message.correction !== null && (
              <p className="vl-texting__correction">Better: {message.correction}</p>
            )}
          </li>
        ))}
      </ol>

      <form
        className="vl-texting__composer"
        onSubmit={(event) => {
          event.preventDefault();
          send();
        }}
      >
        <label className="vl-field">
          <span className="vl-field__label">Your message</span>
          <input
            className="vl-field__input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="running late, sorry!"
            autoComplete="off"
          />
        </label>
        <Button type="submit" disabled={draft.trim().length === 0}>
          Send
        </Button>
      </form>

      <Button
        variant="ghost"
        onClick={() => {
          report();
          onNavigate('/talk');
        }}
      >
        End the chat
      </Button>
    </main>
  );
}
